import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo - Symbol Only */}
            <Link to="/" className="flex items-center" data-testid="home-link" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src="https://customer-assets.emergentagent.com/job_vacation-booking-11/artifacts/s3ptzt3c_InfoXSuites-sima.png" 
                alt="InFox Suites"
                className="h-8 w-auto"
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link
                to="/"
                className="text-stone-700 hover:text-primary transition-colors font-light"
                data-testid="nav-home"
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/properties"
                className="text-stone-700 hover:text-primary transition-colors font-light"
                data-testid="nav-properties"
              >
                {t('nav.properties')}
              </Link>
              <Link
                to="/area-guide"
                className="text-stone-700 hover:text-primary transition-colors font-light"
                data-testid="nav-area-guide"
              >
                {t('nav.areaGuide')}
              </Link>
              <Link
                to="/contact"
                className="text-stone-700 hover:text-primary transition-colors font-light"
                data-testid="nav-contact"
              >
                {t('nav.contact')}
              </Link>
            </nav>

            {/* Language Switcher */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => changeLanguage('el')}
                className={`px-3 py-1 rounded-sm text-sm font-medium transition-colors ${
                  i18n.language === 'el'
                    ? 'bg-primary text-white'
                    : 'text-stone-700 hover:bg-stone-100'
                }`}
                data-testid="lang-greek"
              >
                EL
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 rounded-sm text-sm font-medium transition-colors ${
                  i18n.language === 'en'
                    ? 'bg-primary text-white'
                    : 'text-stone-700 hover:bg-stone-100'
                }`}
                data-testid="lang-english"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Office Information Section */}
      <section className="bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Office Services */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {i18n.language === 'el' ? 'Επισκεφθείτε το Γραφείο μας' : 'Visit Our Office'}
              </h3>
              <p className="text-stone-600 font-light mb-4 leading-relaxed">
                {i18n.language === 'el' 
                  ? 'Οι επισκέπτες μπορούν να επισκεφθούν το γραφείο μας στο κέντρο της πόλης για να αφήσουν αποσκευές ή να λάβουν βοήθεια.'
                  : 'Guests can visit our office in the city center to leave luggage or receive assistance.'}
              </p>
              <div className="space-y-2 text-sm">
                <p className="font-medium text-stone-900">
                  {i18n.language === 'el' ? 'Ωράριο Λειτουργίας:' : 'Office Hours:'}
                </p>
                <p className="text-stone-600">
                  {i18n.language === 'el' ? 'Δευτέρα – Παρασκευή' : 'Monday – Friday'}: 09:00 – 20:00
                </p>
                <p className="text-stone-600">
                  {i18n.language === 'el' ? 'Σάββατο' : 'Saturday'}: 10:00 – 14:00
                </p>
                <p className="text-stone-600">
                  {i18n.language === 'el' ? 'Κυριακή: Κλειστά' : 'Sunday: Closed'}
                </p>
              </div>
            </div>

            {/* Guest Communication Note */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {i18n.language === 'el' ? 'Επικοινωνία με τους Hosts' : 'Communication with Hosts'}
              </h3>
              <p className="text-stone-600 font-light leading-relaxed">
                {i18n.language === 'el'
                  ? 'Η απευθείας τηλεφωνική επικοινωνία με τους hosts είναι διαθέσιμη μετά την επιβεβαίωση της κράτησης. Θα λάβετε πλήρη στοιχεία επικοινωνίας μόλις επιβεβαιωθεί η κράτησή σας.'
                  : 'Direct phone contact with the hosts is available after booking confirmation. You will receive full contact details once your reservation is confirmed.'}
              </p>
              <div className="mt-4 p-4 bg-primary/5 rounded-sm border border-primary/20">
                <p className="text-sm text-primary font-medium">
                  {i18n.language === 'el'
                    ? '💬 Για γενικές ερωτήσεις πριν την κράτηση, χρησιμοποιήστε τη φόρμα επικοινωνίας ή το WhatsApp.'
                    : '💬 For general inquiries before booking, use the contact form or WhatsApp.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary text-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.company')}</h3>
              <div className="text-sm text-white/80 font-light space-y-1">
                <a 
                  href="https://maps.app.goo.gl/24zN6dNBu9e7u79q7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors block"
                >
                  Aristotelous 3
                </a>
                <p>2nd Floor</p>
                <p>Thessaloniki</p>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.contact')}</h3>
              <div className="space-y-2">
                <a
                  href="mailto:infoxsuites@info-x.gr"
                  className="flex items-center space-x-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>infoxsuites@info-x.gr</span>
                </a>
                <a
                  href="tel:+302314033222"
                  className="flex items-center space-x-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  <span>+30 2314 033 222</span>
                </a>
                <a
                  href="https://wa.me/306936547334"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>+30 6936 547 334</span>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('nav.policies')}</h3>
              <div className="space-y-2">
                <Link
                  to="/policies"
                  className="block text-sm text-white/80 hover:text-white transition-colors"
                >
                  {i18n.language === 'el' ? 'Πολιτική Απορρήτου & Όροι' : 'Privacy Policy & Terms'}
                </Link>
                <a
                  href="https://g.page/r/CXtmWrVeJHhfEAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-white/80 hover:text-white transition-colors"
                >
                  {i18n.language === 'el' ? 'Αφήστε μια Κριτική' : 'Leave a Review'}
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 mt-8 pt-8 text-center">
            <p className="text-sm text-white/60">
              © {new Date().getFullYear()} InFox Suites. {t('footer.allRightsReserved')}.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/306936547334"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors z-50"
        data-testid="whatsapp-button"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default Layout;
